/* =========================================================================
   My Surveying Direct, gift engine.

   Bruno, 13 August 2026.

   Why this exists. Shopify's native Buy X Get Y discount does NOT put the gift
   in the cart. The buyer has to add it themselves, and on a $5,189 aircraft
   nobody adds a $269 battery on a hunch. Verified on the live storefront on
   13 Aug: the discount fires correctly at checkout and the offer appears
   nowhere on either product page. So the offer, as it stands, converts nobody.

   This closes that gap without an app subscription, and adds four things the
   apps do not do because they are built for $50 carts, not $50,000 ones:

     1. An advert row that sells in one line: thumbnail, name, the real price
        struck through, "Free gift". Compacted 13 Aug on Joel's read of the
        live panel, which was pushing "Add to cart" off the first screen.
     2. The gift survives the quote path. Institutional buyers are half the
        audience and the higher-value half; an offer that silently dies when
        someone clicks "Request a quote" is an offer for retail only.
     3. The buyer picks the gift, and the pick is recorded. What they choose
        tells us what is actually missing from the box.
     4. Honest accounting. The stated value equals the listed price of the
        gift, and the end date is always on screen.

   No dependencies. Progressive enhancement: with JS off the store still works,
   the buyer simply adds the battery by hand as they do today.
   ========================================================================= */
(function () {
  "use strict";

  /* ---------------------------------------------------------------- config
     Variant IDs read live from the Admin API on 13 Aug 2026.
     `ends` is inclusive and is the same date the Shopify automatic discount
     carries. Keep the two in step: this file controls what the buyer sees,
     the Shopify discount controls what they are charged. If they disagree,
     the buyer sees a promise the checkout does not keep. */
  var OFFERS = [
    {
      id: "m4-battery-2026-09",
      /* Aircraft that unlock the offer. */
      triggers: [50538936893741 /* M4E */, 50538746413357 /* M4T */],
      /* What the buyer can choose from. First entry is the default. */
      gifts: [
        {
          variant: 52289240596781,
          name: "DJI Matrice 4 Series Intelligent Flight Battery",
          value: 269,
          note: "BPX230, 6768 mAh, 22.14 V",
          image: "https://cdn.shopify.com/s/files/1/0849/6840/7341/files/10330264404269.jpg?v=1786594349"
        }
      ],
      ends: "2026-09-30",
      /* One line, eyebrow and headline merged. The panel sits above the buy
         button and every line it takes is a line of "Add to cart" pushed off
         the first screen. */
      headline: "Included with this aircraft: your second flight battery is on us.",
      mechanic:
        "Added to your cart automatically. The discount is applied at " +
        "checkout, so it costs you nothing.",
      /* Backed by Shopify automatic discount 1712622272813, created 13 Aug. */
      enabled: true
    },

    /* ---------------------------------------------------------------------
       M400 + Zenmuse L3 bundle. The flagship application of the whole idea:
       our own listing tells the buyer this system "will not fly as quoted"
       because it ships without a flight battery and without anything that
       charges one. Nobody else in the field admits that, so nobody else is
       positioned to then close it. A TB100 is 6.2% of a $30,649 sale, in the
       same territory as the M4 offer.

       DISABLED until the matching Shopify automatic discount exists. The API
       token carries no discount scopes, so that has to be created in the admin.
       Turning this on before the discount exists would put a promise on the
       page that checkout does not keep, which is the one failure mode this
       whole build is designed to avoid. --------------------------------- */
    {
      id: "m400-tb100-2026-09",
      triggers: [52269317030189 /* M400 + Zenmuse L3 LiDAR bundle */],
      gifts: [
        {
          variant: 52289240367405,
          name: "DJI Matrice 400 TB100 Intelligent Flight Battery",
          value: 1892,
          note: "20,254 mAh, 977 Wh, Li-ion 13S",
          image: "https://cdn.shopify.com/s/files/1/0849/6840/7341/files/10330264207661.jpg?v=1786594345"
        }
      ],
      ends: "2026-09-30",
      headline: "Included with this system: your first flight battery is on us.",
      mechanic:
        "Added to your cart automatically. The discount is applied at " +
        "checkout, so it costs you nothing.",

      /* Net-price line, added 13 Aug 2026.

         This bundle is priced at exactly the sum of its three components. That
         is worth saying out loud, because a buyer at $30,649 will check, and
         the checking is where a bundle SKU usually loses. Showing the working
         turns the check into a reason to buy rather than a reason to pause.

         NOTE, and this matters: an earlier pass claimed the bundle was $907
         MORE than its parts and that the gift made it $985 cheaper. That was
         wrong. It compared against a basket containing a $1,892 TB100 in place
         of the $2,799 D-RTK 3 that is actually in this SKU. Different kit, and
         the $907 is just the difference between those two components. Do not
         reinstate that framing.

         Prices live here as data, not prose, so the sum and the net are
         computed and cannot drift apart from each other. They can still drift
         from the catalogue, so 143_bundle_net_price.py verifies every figure
         below against the live Admin API and refuses to deploy on a mismatch.
         If you change a price in Shopify, that script will tell you. */
      net: {
        components: [
          { variant: 51488234078509, label: "aircraft",     price: 10300 },
          { variant: 52268983025965, label: "payload",      price: 17400 },
          { variant: 52269306413357, label: "base station", price: 2799 }
        ]
      },
      enabled: true
    },

    /* ---------------------------------------------------------------------
       Bare Matrice 400, $10,450. A TB100 against it is 18.1% of the sale,
       against 3.4% to 5.2% on the M4 offer and 6.2% on the bundle. Raised with
       Joel on 13 Aug with those figures; his call, "do for both". Recorded
       here so the number is never a surprise to whoever reads this next.

       There is no cheaper compatible gift in the catalogue: the next options
       are the BS100 at $1,925 and the D-RTK 3 at $2,799, both worse.
       --------------------------------------------------------------------- */
    {
      id: "m400-bare-tb100-2026-09",
      triggers: [51488234078509 /* DJI Matrice 400, No Batteries / No Care */],
      gifts: [
        {
          variant: 52289240367405,
          name: "DJI Matrice 400 TB100 Intelligent Flight Battery",
          value: 1892,
          note: "20,254 mAh, 977 Wh, Li-ion 13S",
          image: "https://cdn.shopify.com/s/files/1/0849/6840/7341/files/10330264207661.jpg?v=1786594345"
        }
      ],
      ends: "2026-09-30",
      /* This SKU ships without a battery, so the gift is not a sweetener here.
         It is the part that makes the aircraft fly at all. */
      headline: "Included with this aircraft: your flight battery is on us.",
      mechanic:
        "Added to your cart automatically. The discount is applied at " +
        "checkout, so it costs you nothing.",
      enabled: true
    }
  ];

  var MONEY = function (n) {
    return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  /* Whole dollars, for the net-price equation. Four figures with ".00" after
     each one reads as a receipt; without, it reads as arithmetic. */
  var MONEY0 = function (n) {
    return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
  };
  var DISMISS_KEY = "mysd-gift-dismissed";
  var CHOICE_KEY = "mysd-gift-choice";
  var busy = false;

  /* ------------------------------------------------------------- utilities */
  function j(url, opts) {
    return fetch(url, Object.assign({ credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" } }, opts || {}))
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); });
  }
  function getCart() { return j("/cart.js"); }
  function addLine(variantId, props) {
    return j("/cart/add.js", { method: "POST",
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: props }] }) });
  }
  function removeLine(key) {
    return j("/cart/change.js", { method: "POST",
      body: JSON.stringify({ id: key, quantity: 0 }) });
  }
  function live(offer) {
    /* `enabled` is the hard gate and it exists for one reason: an offer must
       never appear on the page before the Shopify discount that honours it
       exists. Date-only comparison after that, in the buyer's own timezone,
       because an offer expiring at an ambiguous instant argues with somebody. */
    if (offer.enabled === false) return false;
    var end = new Date(offer.ends + "T23:59:59");
    return !isNaN(end) && new Date() <= end;
  }
  function dismissed(id) {
    try { return (sessionStorage.getItem(DISMISS_KEY) || "").indexOf(id) > -1; }
    catch (e) { return false; }
  }
  function dismiss(id) {
    try {
      var v = sessionStorage.getItem(DISMISS_KEY) || "";
      if (v.indexOf(id) < 0) sessionStorage.setItem(DISMISS_KEY, v + "|" + id);
    } catch (e) {}
  }
  /* Set when a gift turned out not to be discounted. Hides the panel too, so
     the page stops advertising something checkout will not honour. */
  var SUPPRESS_KEY = "mysd-gift-unbacked";
  function suppress(id) {
    try {
      var v = sessionStorage.getItem(SUPPRESS_KEY) || "";
      if (v.indexOf(id) < 0) sessionStorage.setItem(SUPPRESS_KEY, v + "|" + id);
    } catch (e) {}
    var el = document.querySelector('.mysd-gift[data-offer="' + id + '"]');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }
  function suppressed(id) {
    try { return (sessionStorage.getItem(SUPPRESS_KEY) || "").indexOf(id) > -1; }
    catch (e) { return false; }
  }

  function chosenGift(offer) {
    var want;
    try { want = parseInt(sessionStorage.getItem(CHOICE_KEY + "-" + offer.id), 10); } catch (e) {}
    for (var i = 0; i < offer.gifts.length; i++) {
      if (offer.gifts[i].variant === want) return offer.gifts[i];
    }
    return offer.gifts[0];
  }
  function chooseGift(offer, variantId) {
    try { sessionStorage.setItem(CHOICE_KEY + "-" + offer.id, String(variantId)); } catch (e) {}
  }

  /* --------------------------------------------------------- cart matching */
  function cartHasTrigger(cart, offer) {
    return cart.items.some(function (it) { return offer.triggers.indexOf(it.variant_id) > -1; });
  }
  function giftLines(cart, offer) {
    return cart.items.filter(function (it) {
      return it.properties && it.properties._gift_offer === offer.id;
    });
  }

  /* ---------------------------------------------------------- verifyFree
     The safety interlock, and the reason this engine can be switched on before
     its Shopify discount exists.

     The page promises the gift costs nothing. Only the discount can keep that
     promise, and the storefront cannot read discounts directly. So it reads the
     result instead: after the gift is in the cart, check the line actually
     carries a discount covering its full price. If it does not, the line is
     pulled straight back out and the offer suppresses itself for the session.

     Fail closed. A buyer who never sees the offer has lost nothing. A buyer who
     reaches checkout with an unexpected $1,892 on the bill has lost trust, and
     on a purchase this size that is not recoverable. */
  function verifyFree(offer) {
    return getCart().then(function (cart) {
      var lines = giftLines(cart, offer);
      if (!lines.length) return;
      var bad = lines.filter(function (l) {
        var allocated = (l.line_level_discount_allocations || [])
          .reduce(function (t, d) { return t + (d.amount || 0); }, 0);
        return allocated < l.original_line_price;
      });
      if (!bad.length) return;
      var chain = Promise.resolve();
      bad.forEach(function (l) {
        chain = chain.then(function () { return removeLine(l.key); });
      });
      return chain.then(function () {
        dismiss(offer.id);
        suppress(offer.id);
        document.dispatchEvent(new CustomEvent("mysd:gift:unbacked",
          { detail: { offer: offer.id } }));
      });
    });
  }

  /* ------------------------------------------------------------ reconcile
     One job: make the cart agree with the offer rules, and never fight the
     buyer. If they deliberately removed the gift this session, leave it out. */
  function reconcile() {
    if (busy) return Promise.resolve();
    busy = true;
    return getCart().then(function (cart) {
      var chain = Promise.resolve();
      OFFERS.forEach(function (offer) {
        if (!live(offer)) return;
        var has = cartHasTrigger(cart, offer);
        var lines = giftLines(cart, offer);
        var want = chosenGift(offer);

        if (has && lines.length === 0 && !dismissed(offer.id)) {
          chain = chain.then(function () {
            return addLine(want.variant, {
              _gift_offer: offer.id,
              _gift_value: want.value,
              "Included free": offer.headline
            });
          }).then(function () { return verifyFree(offer); });
        } else if (has && lines.length === 1 && lines[0].variant_id === want.variant) {
          /* Already there and it is the right one. Still check it is actually
             free: a discount can be deactivated or expire mid-session, and a
             gift line quietly charging full price is far worse than no offer. */
          chain = chain.then(function () { return verifyFree(offer); });
        } else if (!has && lines.length) {
          /* Aircraft went, so the gift goes. Leaving a $269 battery behind in
             a cart the buyer thought they had emptied is how you lose trust
             at checkout. */
          lines.forEach(function (l) {
            chain = chain.then(function () { return removeLine(l.key); });
          });
        } else if (has && lines.length > 1) {
          lines.slice(1).forEach(function (l) {
            chain = chain.then(function () { return removeLine(l.key); });
          });
        } else if (has && lines.length === 1 && lines[0].variant_id !== want.variant) {
          /* Buyer changed their pick. */
          chain = chain.then(function () { return removeLine(lines[0].key); })
                       .then(function () {
                         return addLine(want.variant, {
                           _gift_offer: offer.id,
                           _gift_value: want.value,
                           "Included free": offer.headline
                         });
                       });
        }
      });
      return chain;
    }).then(function () {
      busy = false;
      document.dispatchEvent(new CustomEvent("mysd:gift:reconciled"));
    }).catch(function () { busy = false; });
  }

  /* ----------------------------------------------------------------- panel
     Rendered on the product page of any triggering aircraft. Placed directly
     above the buy button, because that is where the last hesitation happens. */
  function offerForProduct(variantIds) {
    for (var i = 0; i < OFFERS.length; i++) {
      if (!live(OFFERS[i]) || suppressed(OFFERS[i].id)) continue;
      for (var v = 0; v < variantIds.length; v++) {
        if (OFFERS[i].triggers.indexOf(variantIds[v]) > -1) return OFFERS[i];
      }
    }
    return null;
  }

  function endLabel(offer) {
    var d = new Date(offer.ends + "T12:00:00");
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  }

  /* The net-price line. Built from offer.net.components so the sum and the
     effective cost are computed rather than typed; the only way they can be
     wrong is if a component price in config no longer matches the catalogue,
     which is what the deploy script's verification gate is for.

     Returns "" when an offer has no net block, which is every offer except the
     bundle. The M4 and bare-M400 SKUs are single items, so there is no sum of
     parts to show and the line would be noise. */
  function netLine(offer, gift) {
    var n = offer.net;
    if (!n || !n.components || n.components.length < 2) return "";
    var sum = n.components.reduce(function (t, c) { return t + c.price; }, 0);
    var effective = sum - gift.value;
    return '<p class="mysd-gift__net">' +
      MONEY0(sum) + ' is exactly what the ' +
      n.components.map(function (c) { return c.label; }).join(", ").replace(/, ([^,]*)$/, " and $1") +
      ' come to bought separately (' +
      n.components.map(function (c) { return MONEY0(c.price); }).join(" + ") +
      '). With the ' + MONEY0(gift.value) + ' ' + (n.giftShortName || "TB100") +
      ' included, the effective cost is ' + MONEY0(effective) + '.' +
      '</p>';
  }

  function renderPanel(offer, mount) {
    var gift = chosenGift(offer);
    var el = document.createElement("div");
    el.className = "mysd-gift";
    el.setAttribute("data-offer", offer.id);

    var picker = "";
    if (offer.gifts.length > 1) {
      picker = '<div class="mysd-gift__pick" role="radiogroup" aria-label="Choose your included item">' +
        offer.gifts.map(function (g) {
          return '<button type="button" class="mysd-gift__opt' +
            (g.variant === gift.variant ? " is-on" : "") + '" data-variant="' + g.variant + '" ' +
            'role="radio" aria-checked="' + (g.variant === gift.variant) + '">' +
            '<span class="mysd-gift__opt-name">' + g.name + '</span></button>';
        }).join("") + "</div>";
    }

    /* Advert row: thumbnail, title, struck price, FREE GIFT. The struck price
       does the work a paragraph used to do, and it does it in one line. */
    var thumb = gift.image
      ? '<img class="mysd-gift__img" src="' + gift.image + '&width=160" alt="" loading="lazy" width="152" height="114">'
      : '';

    el.innerHTML =
      '<p class="mysd-gift__head">' + offer.headline + '</p>' +
      picker +
      '<div class="mysd-gift__row">' +
        thumb +
        '<div class="mysd-gift__meta">' +
          '<span class="mysd-gift__name">' + gift.name + '</span>' +
          '<span class="mysd-gift__prices">' +
            '<s class="mysd-gift__was">' + MONEY(gift.value) + '</s>' +
            '<span class="mysd-gift__free">Free gift</span>' +
          '</span>' +
        '</div>' +
      '</div>' +
      '<p class="mysd-gift__mech">' + offer.mechanic + '</p>' +
      netLine(offer, gift) +
      '<p class="mysd-gift__terms">Ends ' + endLabel(offer) + '. Applies to ' +
        'quoted and purchase order sales too, just ask.</p>';

    mount.parentNode.insertBefore(el, mount);

    el.addEventListener("click", function (ev) {
      var b = ev.target.closest ? ev.target.closest(".mysd-gift__opt") : null;
      if (!b) return;
      chooseGift(offer, parseInt(b.getAttribute("data-variant"), 10));
      el.parentNode.removeChild(el);
      renderPanel(offer, mount);
      reconcile();
    });
  }

  /* ------------------------------------------------------------ quote path
     Edge 2. Every gift app on the marketplace assumes a card checkout. Half of
     this audience buys on a purchase order through a quote form, and an offer
     that vanishes the moment somebody clicks "Request a quote" is an offer for
     retail only. This stamps the gift onto the quote so the sales side sees it
     and the buyer is not told one thing on the page and another on the PDF. */
  function stampQuoteForms(offer) {
    var gift = chosenGift(offer);
    var forms = document.querySelectorAll('form[action*="contact"], form[data-quote], form#quote');
    Array.prototype.forEach.call(forms, function (f) {
      if (f.querySelector('[name="contact[gift_offer]"]')) return;
      var h = document.createElement("input");
      h.type = "hidden";
      h.name = "contact[gift_offer]";
      h.value = offer.headline + " (" + gift.name + ", " + MONEY(gift.value) +
                " value, ends " + offer.ends + ")";
      f.appendChild(h);
    });
  }

  /* ----------------------------------------------------------------- boot */
  function currentVariantIds() {
    var ids = [];
    document.querySelectorAll('form[action*="/cart/add"] [name="id"]').forEach(function (n) {
      var v = parseInt(n.value, 10);
      if (!isNaN(v)) ids.push(v);
      if (n.tagName === "SELECT") {
        Array.prototype.forEach.call(n.options, function (o) {
          var ov = parseInt(o.value, 10);
          if (!isNaN(ov)) ids.push(ov);
        });
      }
    });
    return ids;
  }

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  ready(function () {
    var offer = offerForProduct(currentVariantIds());
    if (offer) {
      var mount = document.querySelector('form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] button');
      if (mount) renderPanel(offer, mount);
      stampQuoteForms(offer);
    }

    reconcile();

    /* Re-check after any add to cart, whatever fires it. The theme has no
       cart drawer, so a form submit is a page load and this is belt and
       braces for any future AJAX cart. */
    document.addEventListener("submit", function (e) {
      var f = e.target;
      if (f && f.action && f.action.indexOf("/cart/add") > -1) setTimeout(reconcile, 900);
    }, true);

    /* Buyer removed the gift on the cart page. Respect it for the session
       rather than silently putting it back, which reads as a store arguing
       with its customer. */
    document.addEventListener("click", function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var row = t.closest("[data-gift-offer-row]");
      if (row && (t.matches("[data-remove], .cart__remove, a[href*='quantity=0']"))) {
        dismiss(row.getAttribute("data-gift-offer-row"));
      }
    }, true);
  });

  /* Exposed for the cart template and for debugging in the console. */
  window.MYSDGift = { offers: OFFERS, reconcile: reconcile };
})();
